import { useCallback, useState } from "react";
import { INITIAL_FORM_STATE } from "../utils/userConfigs";
import {
  validateUserForm,
  formatPhoneNumber,
  formatEmail,
  formatString,
  formatPassword,
  formatToAlphanumericOrSpecialCharacters,
} from "../utils/userHelper";

export const useUserForm = () => {
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    switch (name) {
      case "contact_number":
        formattedValue = formatPhoneNumber(value);
        break;
      case "personal_number":
        formattedValue = formatPhoneNumber(value);
        break;
      case "name":
        formattedValue = formatString(value, 50);
        break;
      case "username":
        formattedValue = value;
        break;
      case "email":
        formattedValue = formatEmail(value);
        break;
      case "password":
        formattedValue = value;
        break;
      case "confirm_password":
        formattedValue = value;
        break;
      case "employee_id":
        formattedValue = value;
        break;
      case "father_name":
        formattedValue = formatString(value, 50);
        break;
      case "mother_name":
        formattedValue = formatString(value, 50);
        break;
      case "verification_id":
        formattedValue = formatString(value, 50);
        break;
      default:
        break;
    }

    setForm((prev) => ({ ...prev, [name]: formattedValue }));

    setFormErrors((prev) => ({ ...prev, [name]: [] }));
    setGeneralError("");
  };

  const handleMultiselectChange = useCallback((name, selectedOptions) => {
    setForm((prev) => ({ ...prev, [name]: selectedOptions }));
    setFormErrors((prev) => ({ ...prev, [name]: [] }));
    setGeneralError("");
  }, []);

  const handleImageChange = (file) => {
    if (file) {
      const maxSize = 1 * 1024 * 1024;
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (file.size > maxSize) {
        setFormErrors((prev) => ({
          ...prev,
          image: ["Image size must be less than 1MB"],
        }));
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        setFormErrors((prev) => ({
          ...prev,
          image: ["Image must be in JPEG, PNG, GIF, or WebP format"],
        }));
        return;
      }

      setForm((prev) => ({ ...prev, image: file }));
      setFormErrors((prev) => ({ ...prev, image: [] }));
    }
  };

  const validateForm = (options = {}) => {
    const errors = validateUserForm(form, options);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM_STATE);
    setFormErrors({});
    setGeneralError("");
  }, []);

  const setFormData = useCallback((payload = {}) => {
    setForm((prev) => ({
      ...prev,
      ...payload,
    }));
    setFormErrors({});
    setGeneralError("");
  }, []);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const setApiErrors = useCallback((apiErrorResponse) => {
    if (apiErrorResponse && apiErrorResponse.message) {
      setFormErrors(apiErrorResponse.message);
    } else {
      setGeneralError(
        typeof apiErrorResponse === "string"
          ? apiErrorResponse
          : "An error occurred"
      );
    }
  }, []);

  const clearFieldError = useCallback((fieldName) => {
    setFormErrors((prev) => ({ ...prev, [fieldName]: [] }));
  }, []);

  return {
    form,
    formErrors,
    generalError,
    handleInputChange,
    handleMultiselectChange,
    handleImageChange,
    validateForm,
    resetForm,
    setFormData,
    updateField,
    setApiErrors,
    setGeneralError,
    clearFieldError,
  };
};
